import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/Seo';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sparkles, Lock, Bell } from 'lucide-react';
import { findTool, TOOLS } from '@/lib/tools';
import { ToolCard } from '@/components/tools/ToolCard';

export default function ComingSoonToolPage() {
    const { slug } = useParams();
    const tool = findTool(slug);

    if (!tool) {
        return (
            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16">
                <Seo title="Tool not found" canonical={`/tools/${slug}`} />
                <h1 className="text-3xl font-semibold text-slate-900">Tool not found</h1>
                <p className="mt-2 text-slate-600">
                    We couldn’t find a tool with that name.
                </p>
                <Button asChild className="mt-6">
                    <Link to="/tools">Back to all tools</Link>
                </Button>
            </section>
        );
    }

    const Icon = tool.icon;
    const related = TOOLS.filter((t) => t.category === tool.category && t.slug !== tool.slug).slice(0, 3);

    return (
        <>
            <Seo
                title={`${tool.title} — coming soon`}
                description={`${tool.short} — launching soon on UtilityTool.`}
                canonical={`/tools/${tool.slug}`}
            />
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14">
                <Breadcrumb items={[{ label: 'Tools', to: '/tools' }, { label: tool.title }]} />
                <div className="mt-6 rounded-2xl border border-border/70 bg-white p-8 sm:p-10">
                    <div className="flex items-start gap-4">
                        <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                            <Icon className="w-6 h-6" />
                        </span>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                                    {tool.title}
                                </h1>
                                <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--primary))] text-xs font-medium px-2 py-1">
                                    <Sparkles className="w-3 h-3" /> Coming soon
                                </span>
                            </div>
                            <p className="mt-2 text-slate-600 max-w-2xl">{tool.short}</p>
                        </div>
                    </div>
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <InfoBlock icon={Lock} title="Why it’s not live yet" body="We ship one tool at a time so each one is genuinely useful, not a rushed placeholder." />
                        <InfoBlock icon={Bell} title="Want to be first?" body="Bookmark this page. We’ll roll this tool out publicly as soon as it’s tested and polished." />
                        <InfoBlock icon={Sparkles} title="Meanwhile" body="Explore our live tools — they’re free, fast, and don’t require an account." />
                    </div>
                    <div className="mt-8 flex gap-3">
                        <Button asChild className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--link-hover))] text-white">
                            <Link to="/tools">Explore live tools</Link>
                        </Button>
                        <Button asChild variant="secondary">
                            <Link to="/blog">Read the blog</Link>
                        </Button>
                    </div>
                </div>

                {related.length > 0 ? (
                    <div className="mt-10">
                        <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-slate-900">
                            More in {tool.category} tools
                        </h2>
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                            {related.map((t) => (
                                <ToolCard key={t.slug} tool={t} />
                            ))}
                        </div>
                    </div>
                ) : null}
            </section>
        </>
    );
}

function InfoBlock({ icon: Icon, title, body }) {
    return (
        <Card className="p-4 border-border/70">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-slate-100 text-slate-700">
                <Icon className="w-4 h-4" />
            </span>
            <p className="mt-3 text-sm font-semibold text-slate-900">{title}</p>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed">{body}</p>
        </Card>
    );
}
